//mui
import { createFilterOptions } from '@mui/material/Autocomplete'
import {
  Autocomplete,
  Typography,
  TextField,
  Stack,
  Checkbox,
} from '@mui/material'
//react/redux/translations
import { type ReactElement, useCallback, useEffect, useState } from 'react'

import { type AppState } from 'state/index'
import { useTranslation } from 'react-i18next'
import { useSelector, useDispatch } from 'react-redux'
//Icons
import {
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
  CheckBox as CheckBoxIcon,
} from '@mui/icons-material'
//types

import { type ClientModel } from 'state/client'

import { type FarmModel, type FarmOptionModel } from 'state/farm'
import { TEST_IDS } from 'test-ids'

export type FarmSelectorProps = {
  client?: ClientModel
  selectedFarms?: FarmOptionModel[]
  onChange?: Function
  hideLabel?: boolean
}
export default function FarmSelector(props: FarmSelectorProps): ReactElement {
  const icon = <CheckBoxOutlineBlankIcon fontSize="small" />
  const checkedIcon = <CheckBoxIcon fontSize="small" />
  const { t } = useTranslation()
  const filter = createFilterOptions({
    stringify: (option: FarmOptionModel) => option.name,
  })
  const client: ClientModel | undefined = useSelector(
    (state: AppState) => state.client.singleton,
  )
  const farms: FarmModel[] = useSelector(
    (state: AppState) => state.farm.collection,
  )
  const [filteredFarms, setFilteredFarms] = useState<FarmOptionModel[]>([])
  const [selectedFarms, setSelectedFarms] = useState<FarmOptionModel[]>([])

  const allSelected = filteredFarms.length === selectedFarms.length
  useEffect(() => {
    if (props.client === null && client) {
      setFilteredFarms(client.farms)
      setSelectedFarms(client.farms)
    } else {
      if (props.client?.farms != null) {
        setFilteredFarms(props.client.farms)
        setSelectedFarms(props.client.farms)
      } else {
        setFilteredFarms([])
        setSelectedFarms([])
      }
    }
  }, [client, props.client])

  const handleSelectAll = (isSelected) => {
    if (isSelected) {
      setSelectedFarms(filteredFarms)
    } else {
      handleClearOptions()
    }
  }

  const handleClearOptions = () => {
    setSelectedFarms([])
  }

  const handleToggleSelectAll = () => {
    handleSelectAll && handleSelectAll(!allSelected)
  }
  useEffect(() => {
    if (props.selectedFarms !== undefined) {
      setSelectedFarms(props.selectedFarms)
    } else {
      setSelectedFarms([])
    }
  }, [props.selectedFarms])

  const onFarmChange = useCallback(
    (event, newValue, reason) => {
      if (props.onChange) {
        props.onChange(event, newValue, reason)
      } else {
        let _farms: FarmOptionModel[] = []
        if (reason === 'selectOption' || reason === 'removeOption') {
          if (newValue.find((option) => option.id === 0)) {
            handleToggleSelectAll()
            _farms = !allSelected ? filteredFarms : []
          } else {
            _farms = newValue?.length ? newValue : []
            setSelectedFarms(newValue)
          }
        } else if (reason === 'clear') {
          handleClearOptions()
          _farms = []
        }
        setSelectedFarms(_farms)
      }
    },
    [props.onChange],
  )
  return (
    <Stack spacing={1}>
      {props?.hideLabel ? null : <Typography>{t('farms') + ':'}</Typography>}
      <Autocomplete
        fullWidth
        style={{ maxHeight: '30px' }}
        sx={{
          '& input': {
            height: '1em',
          },
          '& label': {
            lineHeight: '1.5em',
            fontSize: '1em',
          },
        }}
        multiple
        limitTags={2}
        id={TEST_IDS.farms}
        data-testid={TEST_IDS.farms}
        size="small"
        options={filteredFarms}
        getOptionLabel={(option) => option?.name || ''}
        value={selectedFarms}
        disableCloseOnSelect
        isOptionEqualToValue={(option, value) => {
          if (Array.isArray(value)) {
            return value.findIndex((element) => element.id === option.id) >= 0
          } else {
            return option.id === value.id
          }
        }}
        onChange={(event, newValue, reason) => {
          onFarmChange(event, newValue, reason)
        }}
        filterOptions={(options, params) => {
          const filtered: FarmOptionModel[] = filter(options, params)
          return [
            {
              name: t('select_all'),
              id: 0,
            },
            ...filtered,
          ]
        }}
        renderOption={(props, option, { selected }) => {
          const selectAllProps =
            option.id === 0 // To control the state of 'select-all' checkbox
              ? { checked: allSelected }
              : {}
          return (
            <li style={{ padding: '0 0 0 5px' }} {...props} key={option.id}>
              <Checkbox
                icon={icon}
                checkedIcon={checkedIcon}
                style={{ marginRight: 8 }}
                checked={selected}
                {...selectAllProps}
              />
              {option.name}
            </li>
          )
        }}
        renderInput={(params) => {
          return props.hideLabel ? (
            //@ts-ignore
            <TextField
              {...params}
              label={t('farms')}
              placeholder={
                selectedFarms && selectedFarms.length > 0 ? '' : t('farms')
              }
              style={{
                width: '100%',
                maxHeight: '35px',
                textOverflow: 'ellipsis',
              }}
            />
          ) : (
            //@ts-ignore
            <TextField
              {...params}
              placeholder={
                selectedFarms && selectedFarms.length > 0 ? '' : t('farms')
              }
              style={{
                width: '100%',
                maxHeight: '35px',
                textOverflow: 'ellipsis',
              }}
            />
          )
        }}
      />
    </Stack>
  )
}
